import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { 
  Plus as PlusIcon, 
  Search as MagnifyingGlassIcon,
  Calendar as CalendarIcon,
  Users as UserGroupIcon,
  FileText as DocumentTextIcon,
  Clock as ClockIcon,
  CheckCircle as CheckCircleIcon,
  XCircle as XCircleIcon,
  Pencil as PencilIcon,
  Trash2 as TrashIcon,
  MoreVertical as MoreVerticalIcon,
  ArrowRight as ArrowRightIcon
} from 'lucide-react';

const Meetings = () => {
  const navigate = useNavigate();
  const { currentUser, hasPermission } = useAuth();
  const { meetings, users, departments, deleteMeeting, createMeeting, createNotification } = useData();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, in-progress, completed, scheduled
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [openMenuId, setOpenMenuId] = useState(null);
  
  // State for scheduling future meeting
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleMeetingData, setScheduleMeetingData] = useState({
    subject: '',
    purpose: '',
    mainDepartmentId: '',
    participantIds: [],
    startDate: '',
    endDate: '',
    location: ''
  });
  const [saving, setSaving] = useState(false);

  // Check if user can create meetings (only CEO and Deputy CEO)
  const canCreateMeeting = hasPermission('deputy_ceo');
  const canEdit = hasPermission('deputy_ceo');

  // Filter meetings
  const filteredMeetings = useMemo(() => {
    return meetings.filter(meeting => {
      // Search filter
      const matchesSearch = !searchTerm || 
        meeting.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        meeting.purpose?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Status filter
      const matchesStatus = statusFilter === 'all' || meeting.status === statusFilter;
      
      // Department filter
      const matchesDepartment = departmentFilter === 'all' || 
        meeting.mainDepartmentId === departmentFilter;
      
      return matchesSearch && matchesStatus && matchesDepartment;
    });
  }, [meetings, searchTerm, statusFilter, departmentFilter]);

  // Get user name by ID
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || user?.email || 'לא ידוע';
  };

  // Get department name by ID
  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || 'לא ידוע';
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date?.toDate ? date.toDate() : new Date(date);
    return new Intl.DateTimeFormat('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(dateObj);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    if (status === 'completed') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-4 h-4 ml-1" />
          הסתיימה
        </span>
      );
    }
    if (status === 'scheduled') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <CalendarIcon className="w-4 h-4 ml-1" />
          מתוכננת
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        <ClockIcon className="w-4 h-4 ml-1" />
        בתהליך
      </span>
    );
  };

  // Handle delete meeting
  const handleDelete = async (e, meetingId) => {
    e.stopPropagation(); // Prevent navigation when clicking delete
    
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את הישיבה? פעולה זו לא ניתנת לביטול.')) {
      return;
    }

    try {
      await deleteMeeting(meetingId);
      alert('הישיבה נמחקה בהצלחה');
    } catch (error) {
      console.error('Error deleting meeting:', error);
      alert('שגיאה במחיקת הישיבה');
    }
  };

  // Handle edit meeting
  const handleEdit = (e, meetingId) => {
    e.stopPropagation(); // Prevent navigation when clicking edit
    navigate(`/meetings/edit/${meetingId}`);
  };

  const handleFollowUpMeeting = (e, previousMeetingId) => {
    e.stopPropagation(); // Prevent navigation when clicking follow-up
    navigate(`/meetings/new?followUp=${previousMeetingId}`);
  };

  // Handle scheduling future meeting
  const handleScheduleMeeting = async () => {
    // Validation
    if (!scheduleMeetingData.subject.trim()) {
      alert('נא למלא נושא לישיבה');
      return;
    }
    if (!scheduleMeetingData.mainDepartmentId) {
      alert('נא לבחור מחלקה');
      return;
    }
    if (!scheduleMeetingData.startDate) {
      alert('נא לבחור תאריך ושעת התחלה');
      return;
    }
    if (!scheduleMeetingData.endDate) {
      alert('נא לבחור תאריך ושעת סיום');
      return;
    }
    if (scheduleMeetingData.participantIds.length === 0) {
      alert('נא לבחור לפחות משתתף אחד');
      return;
    }

    // Check that end date is after start date
    if (new Date(scheduleMeetingData.endDate) <= new Date(scheduleMeetingData.startDate)) {
      alert('שעת הסיום חייבת להיות אחרי שעת ההתחלה');
      return;
    }

    setSaving(true);
    try {
      // Create scheduled meeting
      const meetingData = {
        subject: scheduleMeetingData.subject,
        purpose: scheduleMeetingData.purpose,
        mainDepartmentId: scheduleMeetingData.mainDepartmentId,
        participantIds: scheduleMeetingData.participantIds,
        startDate: new Date(scheduleMeetingData.startDate),
        endDate: new Date(scheduleMeetingData.endDate),
        location: scheduleMeetingData.location || '',
        agendaItems: [],
        decisions: [],
        summary: '',
        newTasks: [],
        newProjects: [],
        linkedTaskIds: [],
        linkedProjectIds: [],
        status: 'scheduled', // Mark as scheduled
        facilitatorId: currentUser.id,
        facilitatorName: currentUser.name || currentUser.email,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const meetingId = await createMeeting(meetingData);
      
      // Send notifications to all participants (including the creator!)
      for (const participantId of scheduleMeetingData.participantIds) {
        const isCreator = participantId === currentUser.id;
        await createNotification({
          userId: participantId,
          type: 'meeting_scheduled',
          title: isCreator ? 'ישיבה שיצרת' : 'הוזמנת לישיבה',
          message: isCreator 
            ? `יצרת ישיבה: "${scheduleMeetingData.subject}" בתאריך ${new Date(scheduleMeetingData.startDate).toLocaleDateString('he-IL')} בשעה ${new Date(scheduleMeetingData.startDate).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })} - נא לאשר השתתפות`
            : `${currentUser.name} הזמין אותך לישיבה: "${scheduleMeetingData.subject}" בתאריך ${new Date(scheduleMeetingData.startDate).toLocaleDateString('he-IL')} בשעה ${new Date(scheduleMeetingData.startDate).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`,
          meetingId: meetingId,
          actionRequired: true,
          priority: 'medium'
        });
      }

      alert('✅ הישיבה תוכננה בהצלחה! ההתראות נשלחו למשתתפים.');
      
      // Reset form
      setScheduleMeetingData({
        subject: '',
        purpose: '',
        mainDepartmentId: '',
        participantIds: [],
        startDate: '',
        endDate: '',
        location: ''
      });
      setShowScheduleModal(false);
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      alert('❌ שגיאה בתכנון הישיבה');
    } finally {
      setSaving(false);
    }
  };

  // Toggle participant selection
  const toggleParticipant = (userId) => {
    setScheduleMeetingData(prev => {
      const isSelected = prev.participantIds.includes(userId);
      return {
        ...prev,
        participantIds: isSelected
          ? prev.participantIds.filter(id => id !== userId)
          : [...prev.participantIds, userId]
      };
    });
  };

  // Get users by department
  const getUsersByDepartment = (deptId) => {
    return users.filter(user => {
      const userDepts = Array.isArray(user.departmentIds) ? user.departmentIds : 
                       user.departmentId ? [user.departmentId] : [];
      return userDepts.includes(deptId);
    });
  };

  // Get other department users
  const getOtherDepartmentUsers = () => {
    if (!scheduleMeetingData.mainDepartmentId) return [];
    return users.filter(user => {
      const userDepts = Array.isArray(user.departmentIds) ? user.departmentIds : 
                       user.departmentId ? [user.departmentId] : [];
      return !userDepts.includes(scheduleMeetingData.mainDepartmentId);
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
                ישיבות הנהלה
              </h1>
              <p className="text-gray-600 text-lg">
                ניהול וסיכום ישיבות, יצירת משימות ופרויקטים בצורה מסודרת ויעילה
              </p>
            </div>
            {canCreateMeeting && (
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/meetings/new')}
                  className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 font-semibold"
                >
                  <PlusIcon className="w-6 h-6" />
                  ישיבה חדשה
                </button>
                <button
                  onClick={() => setShowScheduleModal(true)}
                  className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 font-semibold"
                >
                  <CalendarIcon className="w-6 h-6" />
                  תכנן ישיבה עתידית
                </button>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100 hover:border-blue-200 group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">סך הכל ישיבות</p>
                  <p className="text-4xl font-bold text-gray-900">{meetings.length}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                  <CalendarIcon className="w-10 h-10 text-blue-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100 hover:border-purple-200 group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">ישיבות מתוכננות</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    {meetings.filter(m => m.status === 'scheduled').length}
                  </p>
                </div>
                <div className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                  <CalendarIcon className="w-10 h-10 text-purple-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100 hover:border-blue-200 group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">ישיבות בתהליך</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    {meetings.filter(m => m.status === 'in-progress').length}
                  </p>
                </div>
                <div className="p-4 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                  <ClockIcon className="w-10 h-10 text-blue-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100 hover:border-green-200 group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">ישיבות שהסתיימו</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                    {meetings.filter(m => m.status === 'completed').length}
                  </p>
                </div>
                <div className="p-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                  <CheckCircleIcon className="w-10 h-10 text-green-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="חיפוש לפי נושא או מטרה..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                />
              </div>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white font-medium"
              >
                <option value="all">כל הסטטוסים</option>
                <option value="scheduled">מתוכננת</option>
                <option value="in-progress">בתהליך</option>
                <option value="completed">הסתיימה</option>
              </select>

              {/* Department Filter */}
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white font-medium"
              >
                <option value="all">כל המחלקות</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Meetings List */}
        {filteredMeetings.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-16 text-center border border-gray-100">
            <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-full w-32 h-32 mx-auto mb-6 flex items-center justify-center">
              <CalendarIcon className="w-20 h-20 text-blue-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">אין ישיבות</h3>
            <p className="text-gray-600 text-lg mb-8 max-w-md mx-auto">
              {searchTerm || statusFilter !== 'all' || departmentFilter !== 'all'
                ? 'לא נמצאו ישיבות התואמות את הסינון שבחרת'
                : 'טרם נוצרו ישיבות במערכת. התחל ביצירת הישיבה הראשונה!'}
            </p>
            {canCreateMeeting && !searchTerm && statusFilter === 'all' && departmentFilter === 'all' && (
              <button
                onClick={() => navigate('/meetings/new')}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
              >
                <PlusIcon className="w-6 h-6" />
                צור ישיבה ראשונה
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredMeetings.map(meeting => (
              <div
                key={meeting.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer relative border border-gray-100 hover:border-blue-200 group overflow-hidden"
                onClick={() => navigate(`/meetings/${meeting.id}`)}
              >
                {/* Gradient accent bar */}
                <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 transform origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                
                <div className="p-8">
                  <div className="flex justify-between items-start mb-5">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {meeting.subject}
                        </h3>
                        {meeting.previousMeetingId && (
                          <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200">
                            <ArrowRightIcon className="w-3 h-3 ml-1" />
                            המשך
                          </span>
                        )}
                        {getStatusBadge(meeting.status)}
                      </div>
                      <p className="text-gray-600 text-lg leading-relaxed">{meeting.purpose}</p>
                    </div>
                    
                    {/* Action Buttons */}
                    {canEdit && (
                      <div className="flex gap-2 mr-4">
                        <button
                          onClick={(e) => handleFollowUpMeeting(e, meeting.id)}
                          className="p-3 hover:bg-green-50 rounded-xl transition-all duration-200 text-green-600 hover:scale-110"
                          title="צור ישיבת המשך"
                        >
                          <ArrowRightIcon className="w-5 h-5" />
                        </button>
                        {meeting.status !== 'completed' && (
                          <button
                            onClick={(e) => handleEdit(e, meeting.id)}
                            className="p-3 hover:bg-blue-50 rounded-xl transition-all duration-200 text-blue-600 hover:scale-110"
                            title="ערוך ישיבה"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDelete(e, meeting.id)}
                          className="p-3 hover:bg-red-50 rounded-xl transition-all duration-200 text-red-600 hover:scale-110"
                          title="מחק ישיבה"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Facilitator */}
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        <UserGroupIcon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">מנחה</p>
                        <p className="text-sm font-semibold text-gray-900">{getUserName(meeting.facilitatorId)}</p>
                      </div>
                    </div>

                    {/* Department */}
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        <DocumentTextIcon className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">מחלקה</p>
                        <p className="text-sm font-semibold text-gray-900">{getDepartmentName(meeting.mainDepartmentId)}</p>
                      </div>
                    </div>

                    {/* Participants Count */}
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        <UserGroupIcon className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">משתתפים</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {meeting.participantIds?.length || 0} אנשים
                        </p>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        <CalendarIcon className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">תאריך</p>
                        <p className="text-sm font-semibold text-gray-900">{formatDate(meeting.startDate)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Tasks & Projects Summary */}
                  {meeting.status === 'completed' && (
                    <div className="mt-6 pt-5 border-t-2 border-gray-100">
                      <div className="flex gap-4 flex-wrap">
                        {meeting.createdTaskIds?.length > 0 && (
                          <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                            <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                            <span className="text-blue-700 font-semibold">
                              {meeting.createdTaskIds.length} משימות נוצרו
                            </span>
                          </div>
                        )}
                        {meeting.createdProjectIds?.length > 0 && (
                          <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                            <CheckCircleIcon className="w-5 h-5 text-green-600" />
                            <span className="text-green-700 font-semibold">
                              {meeting.createdProjectIds.length} פרויקטים נוצרו
                            </span>
                          </div>
                        )}
                        {meeting.linkedTaskIds?.length > 0 && (
                          <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                            <DocumentTextIcon className="w-5 h-5 text-purple-600" />
                            <span className="text-purple-700 font-semibold">
                              {meeting.linkedTaskIds.length} משימות קשורות
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Schedule Meeting Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
                <div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                    תכנון ישיבה עתידית
                  </h2>
                  <p className="text-gray-600 mt-2">
                    קבע ישיבה עתידית ושלח הזמנות למשתתפים
                  </p>
                </div>
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="p-3 hover:bg-gray-100 rounded-2xl transition-all duration-200"
                  disabled={saving}
                >
                  <span className="text-gray-500 text-xl">✕</span>
                </button>
              </div>

              {/* Form */}
              <div className="space-y-6">
                {/* Subject */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    נושא הישיבה <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={scheduleMeetingData.subject}
                    onChange={(e) => setScheduleMeetingData(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    placeholder="למשל: דיון בתקציב רבעון ראשון"
                    disabled={saving}
                  />
                </div>

                {/* Purpose */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    מטרת הישיבה
                  </label>
                  <textarea
                    value={scheduleMeetingData.purpose}
                    onChange={(e) => setScheduleMeetingData(prev => ({ ...prev, purpose: e.target.value }))}
                    className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    rows="3"
                    placeholder="תאר את המטרה והיעדים של הישיבה..."
                    disabled={saving}
                  />
                </div>

                {/* Department */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    מחלקה ראשית <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={scheduleMeetingData.mainDepartmentId}
                    onChange={(e) => setScheduleMeetingData(prev => ({ ...prev, mainDepartmentId: e.target.value, participantIds: [] }))}
                    className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    disabled={saving}
                  >
                    <option value="">בחר מחלקה</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      תאריך ושעת התחלה <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduleMeetingData.startDate}
                      onChange={(e) => setScheduleMeetingData(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                      disabled={saving}
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      תאריך ושעת סיום <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduleMeetingData.endDate}
                      onChange={(e) => setScheduleMeetingData(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                      disabled={saving}
                      min={scheduleMeetingData.startDate || new Date().toISOString().slice(0, 16)}
                    />
                  </div>
                </div>

                {/* Location (Optional) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    מיקום הישיבה (אופציונלי)
                  </label>
                  <input
                    type="text"
                    value={scheduleMeetingData.location}
                    onChange={(e) => setScheduleMeetingData(prev => ({ ...prev, location: e.target.value }))}
                    className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    placeholder="למשל: חדר ישיבות 3, קומה 2"
                    disabled={saving}
                  />
                </div>

                {/* Participants */}
                {scheduleMeetingData.mainDepartmentId && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      משתתפים <span className="text-red-500">*</span>
                      {scheduleMeetingData.participantIds.length > 0 && (
                        <span className="mr-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                          {scheduleMeetingData.participantIds.length} נבחרו
                        </span>
                      )}
                    </label>

                    {/* Department Users */}
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <span className="px-3 py-1 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg text-blue-700">
                          עובדים מ{departments.find(d => d.id === scheduleMeetingData.mainDepartmentId)?.name}
                        </span>
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                        {getUsersByDepartment(scheduleMeetingData.mainDepartmentId).map(user => (
                          <label
                            key={user.id}
                            className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                              scheduleMeetingData.participantIds.includes(user.id)
                                ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg'
                                : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={scheduleMeetingData.participantIds.includes(user.id)}
                              onChange={() => toggleParticipant(user.id)}
                              className="w-5 h-5 text-green-600 rounded-md border-2 border-gray-300"
                              disabled={saving}
                            />
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold shadow-md">
                              {(user.name || user.email).charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{user.name}</p>
                              <p className="text-xs text-gray-500">{user.email}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Other Department Users */}
                    {getOtherDepartmentUsers().length > 0 && (
                      <div className="pt-4 border-t-2 border-gray-100">
                        <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                          <span className="px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg text-purple-700">
                            עובדים ממחלקות אחרות
                          </span>
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                          {getOtherDepartmentUsers().map(user => (
                            <label
                              key={user.id}
                              className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                scheduleMeetingData.participantIds.includes(user.id)
                                  ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 shadow-lg'
                                  : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={scheduleMeetingData.participantIds.includes(user.id)}
                                onChange={() => toggleParticipant(user.id)}
                                className="w-5 h-5 text-purple-600 rounded-md border-2 border-gray-300"
                                disabled={saving}
                              />
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold shadow-md">
                                {(user.name || user.email).charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900">{user.name}</p>
                                <p className="text-xs text-gray-500">{user.email}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!scheduleMeetingData.mainDepartmentId && (
                  <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl">
                    <UserGroupIcon className="w-16 h-16 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium">
                      בחר מחלקה ראשית כדי לראות משתתפים
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-4 justify-end mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-semibold"
                  disabled={saving}
                >
                  ביטול
                </button>
                <button
                  onClick={handleScheduleMeeting}
                  className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-semibold flex items-center gap-2"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      מתכנן...
                    </>
                  ) : (
                    <>
                      <CalendarIcon className="w-5 h-5" />
                      תכנן ישיבה ושלח הזמנות
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Meetings;

